import { EQUIPMENT, sameEquipmentKey } from "@frontier/content/arsenal";
import type { EquipmentKey } from "@frontier/content/arsenal";
import type { EquipmentTier } from "@frontier/content/equipment-tiers";
import type { Profile } from "@frontier/protocol/contracts";
import {
  contextAccepts,
  mountedFocus,
  equipmentCount,
  workshopEquipmentTier,
} from "./equipment-selection";
import type {
  EquipmentContext,
  EquipmentSelection,
} from "./equipment-selection";

export interface EquipmentFocus {
  key: EquipmentKey;
  tier: EquipmentTier;
}

export function installedEquipmentFocus(
  profile: EquipmentSelection,
  context: EquipmentContext,
): EquipmentFocus | null {
  if (profile.shipFitting) return mountedFocus(profile.shipFitting, context);
  if (context.kind === "cell" || context.kind === "utility") return null;
  if (context.kind === "weapon")
    return {
      key: { kind: "weapon", id: profile.weaponId },
      tier: profile.equipmentTiers.weapon,
    };
  if (context.kind === "module")
    return context.slot === 0
      ? {
          key: { kind: "module", id: profile.moduleId },
          tier: profile.equipmentTiers.module,
        }
      : {
          key: { kind: "module", id: profile.secondaryModuleId },
          tier: profile.equipmentTiers.secondaryModule,
        };
  return profile.passiveId === "none"
    ? null
    : {
        key: { kind: "passive", id: profile.passiveId },
        tier: profile.equipmentTiers.passive,
      };
}

export function sameEquipmentFocus(
  left: EquipmentFocus | null,
  right: EquipmentFocus | null,
): boolean {
  return left === null || right === null
    ? left === right
    : left.tier === right.tier && sameEquipmentKey(left.key, right.key);
}

export function resolveEquipmentFocus(
  profile: Profile,
  context: EquipmentContext,
  preferred: EquipmentFocus | null,
): EquipmentFocus | null {
  const installed = installedEquipmentFocus(profile, context);
  if (preferred && !contextAccepts(context, preferred.key)) return installed;
  if (preferred) {
    const { key, tier } = preferred;
    if (equipmentCount(profile.arsenal, key, tier) > 0) return preferred;
    if (equipmentCount(profile.arsenal, key) > 0)
      return {
        key,
        tier: workshopEquipmentTier(profile, context, key, tier),
      };
    if (
      profile.arsenal.knownBlueprints.some((item) =>
        sameEquipmentKey(item, key),
      ) ||
      EQUIPMENT.some((item) => sameEquipmentKey(item.key, key))
    )
      return { key, tier: 0 };
  }
  if (installed) return installed;
  const owned = profile.arsenal.stacks.find(
    (stack) => contextAccepts(context, stack.key) && stack.count > 0,
  );
  if (owned) return { key: owned.key, tier: owned.tier };
  const first = EQUIPMENT.find((item) => contextAccepts(context, item.key));
  return first ? { key: first.key, tier: 0 } : null;
}
